import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Award, Globe, Package, X } from 'lucide-react';
import { useInView } from '../hooks/useInView';

const API = process.env.REACT_APP_BACKEND_URL;

const IMAGES = {
  hero: 'https://customer-assets.emergentagent.com/job_site-creator-1837/artifacts/krpqpbcl_IMG_0549.jpeg',
  // Product 4 from brochure: dark blue premium serum/spray bottle with silver lid
  bottles: 'https://images.unsplash.com/photo-1650112511613-ff877a435291?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
  // Product 2 from brochure: frosted white cream jar with lid
  jars: 'https://images.unsplash.com/photo-1684430127653-c5436b095a34?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
  // Product 1 from brochure: large white lotion pump dispenser bottle
  pumps: 'https://images.unsplash.com/photo-1597931752949-98c74b5b159f?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
  // Colourful cosmetic caps & closures
  caps: 'https://images.unsplash.com/photo-1637730177972-0bb1d3a05c43?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
  // Product 5 from brochure: mascara / colour cosmetics packaging
  cosmetics: 'https://images.unsplash.com/photo-1645961359148-ca3f853de345?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
  mould: 'https://images.unsplash.com/photo-1676646606766-f8325cac7faf?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
  decoration: 'https://static.prod-images.emergentagent.com/jobs/13ebbb87-c8f1-4957-a008-fa06bcff1c9a/images/173cae390679637cab339600ecf6a4ab55bf8efebf1e90465301b8af6c0e67fe.png',
  printing: 'https://images.unsplash.com/photo-1760804876161-ba0337e998fe?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
  about: 'https://images.pexels.com/photos/13516791/pexels-photo-13516791.jpeg?auto=compress&cs=tinysrgb&w=800',
};

const CATEGORIES = [
  { id: 'jars', name: 'Jars', desc: 'Cream, balm & beauty jars', image: 'https://customer-assets.emergentagent.com/job_site-creator-1837/artifacts/0ka489yz_Jars%20website.png' },
  { id: 'bottles', name: 'Bottles', desc: 'Serums, lotions & specialty bottles', image: 'https://customer-assets.emergentagent.com/job_site-creator-1837/artifacts/x4ohc4rp_Bottles%20for%20website.png' },
  { id: 'pumps', name: 'Lotion Pumps', desc: 'Lotion pumps & fine mist sprayers', image: 'https://customer-assets.emergentagent.com/job_site-creator-1837/artifacts/m3o5jx2z_Pump%20website.png' },
  { id: 'caps', name: 'Caps & Closures', desc: 'Disc tops, flip caps & closures', image: 'https://customer-assets.emergentagent.com/job_site-creator-1837/artifacts/ofll9zml_Caps%20website.png' },
  { id: 'cosmetics', name: 'Colour Cosmetics', desc: 'Lipstick cases, compacts & mascaras', image: 'https://customer-assets.emergentagent.com/job_site-creator-1837/artifacts/tf7zckjr_Cc%20website.png' },
];

const CLIENTS = [
  { name: 'Hindustan Unilever', domain: 'hul.co.in' },
  { name: 'Patanjali', domain: 'patanjaliayurved.net' },
  { name: 'Himalaya', domain: 'himalayawellness.com' },
  { name: 'Marico', domain: 'marico.com' },
  { name: 'Emami', domain: 'emamiltd.in' },
  { name: 'Forest Essentials', domain: 'forestessentialsindia.com' },
  { name: 'Kama Ayurveda', domain: 'kamaayurveda.com' },
  { name: 'Lotus Herbals', domain: 'lotusherbals.com' },
  { name: 'Tata', domain: 'tata.com' },
  { name: 'Reliance', domain: 'ril.com' },
  { name: 'Sun Pharma', domain: 'sunpharma.com' },
  { name: 'Torrent Pharma', domain: 'torrentpharma.com' },
  { name: 'Foxtale', domain: 'foxtale.in' },
  { name: 'Jovees Herbal', domain: 'jovees.com' },
  { name: 'Blue Heaven', domain: 'blueheaven.in' },
  { name: 'Alembic Pharma', domain: 'alembicpharmaceuticals.com' },
  { name: 'RAS Luxury Oils', domain: 'rasluxuryoils.com' },
  { name: 'Fab India', domain: 'fabindia.com' },
];


const STATS = [
  { value: '25+', label: 'Years of Excellence' },
  { value: '500+', label: 'Packaging Options' },
  { value: '100%', label: 'Made in India' },
  { value: '1000+', label: 'Clients' },
];

const SERVICES_PREVIEW = [
  { title: 'Custom Mould Making', desc: 'From concept to production-ready tooling. Our in-house mould making ensures precision and speed.', image: IMAGES.mould, icon: Package },
  { title: 'Decoration Finishes', desc: 'Metalizing, spray coating, hot stamping and foiling — transforming packaging into art.', image: IMAGES.decoration, icon: Award },
  { title: 'Precision Printing', desc: 'UV silk screen and pad printing with sharp, vibrant results for premium brand identity.', image: IMAGES.printing, icon: Globe },
];

function reveal(inView) {
  return {
    opacity: inView ? 1 : 0,
    transform: inView ? 'translateY(0)' : 'translateY(2rem)',
    transition: 'opacity 0.7s ease-out, transform 0.7s ease-out',
  };
}

export default function Home() {
  return (
    <main>
      <HeroSection />
      <PopupBanner />
      <ClientLogosSection />
      <CategoriesSection />
      <StatsSection />
      <ServicesSection />
      <AboutSection />
      <CTASection />
    </main>
  );
}

function PopupBanner() {
  const [popup, setPopup] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/popups/active`)
      .then(r => r.json())
      .then(data => { if (data && data.id) setPopup(data); })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!popup || dismissed) return null;

  const typeColors = {
    exhibition: { bg: '#0B1B3D', accent: '#D4AF37' },
    promotion: { bg: '#1a3a1a', accent: '#4ade80' },
    announcement: { bg: '#2d1a0e', accent: '#f97316' },
  };
  const colors = typeColors[popup.popup_type] || typeColors.announcement;

  return (
    <div
      className="relative py-3 px-4 text-center"
      style={{ backgroundColor: colors.bg, borderBottom: `2px solid ${colors.accent}` }}
      data-testid="popup-banner"
    >
      <div className="max-w-4xl mx-auto flex items-center justify-center gap-4 flex-wrap">
        <span className="text-xs tracking-[0.2em] uppercase font-semibold px-2 py-0.5" style={{ backgroundColor: colors.accent, color: colors.bg }}>
          {popup.popup_type}
        </span>
        <p className="text-sm font-light" style={{ color: 'white' }}>
          <strong className="font-semibold">{popup.title}</strong>
          {popup.message && <span style={{ color: 'rgba(255,255,255,0.75)' }}> — {popup.message}</span>}
        </p>
        {popup.button_text && popup.button_link && (
          <Link
            to={popup.button_link}
            className="text-xs tracking-[0.15em] uppercase font-medium px-3 py-1 transition-opacity hover:opacity-80"
            style={{ backgroundColor: colors.accent, color: colors.bg }}
          >
            {popup.button_text}
          </Link>
        )}
      </div>
      <button
        onClick={() => setDismissed(true)}
        className="absolute right-4 top-1/2 -translate-y-1/2 transition-opacity hover:opacity-60"
        style={{ color: 'rgba(255,255,255,0.5)' }}
        aria-label="Dismiss"
        data-testid="popup-dismiss-btn"
      >
        <X size={16} />
      </button>
    </div>
  );
}

function ClientLogosSection() {
  const doubled = [...CLIENTS, ...CLIENTS];
  return (
    <section
      className="py-12 overflow-hidden"
      style={{
        backgroundColor: '#FFFFFF',
        borderTop: '1px solid rgba(11,27,61,0.06)',
        borderBottom: '1px solid rgba(11,27,61,0.06)',
      }}
      data-testid="clients-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 mb-8 text-center">
        <p className="text-[10px] tracking-[0.3em] uppercase font-medium" style={{ color: '#4A4F5A' }}>
          Trusted by India's Leading Brands
        </p>
      </div>
      <div className="marquee-fade overflow-hidden">
        <div className="marquee-track">
          {doubled.map((client, idx) => (
            <ClientLogo key={`${client.name}-${idx}`} client={client} />
          ))}
        </div>
      </div>
    </section>
  );
}

function ClientLogo({ client }) {
  const [imgError, setImgError] = useState(false);
  const faviconSrc = `https://www.google.com/s2/favicons?domain=${client.domain}&sz=128`;

  return (
    <div
      className="flex items-center gap-3 mx-10 group flex-shrink-0 cursor-default"
      title={client.name}
      data-testid={`client-logo-${client.domain}`}
    >
      {!imgError && (
        <img
          src={faviconSrc}
          alt={client.name}
          className="w-7 h-7 object-contain flex-shrink-0 transition-all duration-300"
          style={{ filter: 'grayscale(0%) opacity(1)' }}
          onMouseEnter={e => { e.currentTarget.style.filter = 'grayscale(0%) opacity(1) brightness(1.1)'; }}
          onMouseLeave={e => { e.currentTarget.style.filter = 'grayscale(0%) opacity(1)'; }}
          onError={() => setImgError(true)}
        />
      )}
      <span
        className="whitespace-nowrap font-semibold tracking-wider transition-all duration-300 group-hover:opacity-90"
        style={{
          color: 'rgba(11,27,61,0.38)',
          fontFamily: 'DM Sans, sans-serif',
          fontSize: '0.75rem',
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
        }}
      >
        {client.name}
      </span>
    </div>
  );
}



function HeroSection() {
  return (
    <section className="min-h-screen relative flex items-start overflow-hidden" data-testid="hero-section">
      <div className="absolute inset-0">
        <img src={IMAGES.hero} alt="Premium packaging" className="w-full h-full object-cover" style={{ objectPosition: '65% center' }} />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(100deg, rgba(250,249,246,1.0) 0%, rgba(250,249,246,0.96) 5%, rgba(250,249,246,0.60) 27%, rgba(250,249,246,0.0) 55%)' }} />
      </div>
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full" style={{ paddingTop: 'clamp(100px, 22vh, 200px)', paddingBottom: '80px' }}>
        <div className="max-w-2xl">
          <p className="text-xs tracking-[0.3em] uppercase mb-6 font-medium" style={{ color: '#8B6914' }}>
            Made in India · Global Standards
          </p>
          <h1
            className="font-light leading-none mb-6"
            style={{ fontFamily: 'Cormorant Garamond, serif', color: '#0B1B3D', fontSize: 'clamp(3rem, 7vw, 5.5rem)' }}
          >
            Where Precision<br />Meets Elegance.
          </h1>
          <p className="text-lg font-light leading-relaxed mb-10 max-w-lg" style={{ color: '#4A4F5A' }}>
            Premium plastic packaging solutions — bottles, jars, pumps and colour cosmetics — crafted with precision for the world's most discerning beauty brands.
          </p>
          <div className="flex flex-wrap gap-4">
            <Link
              to="/products"
              className="px-8 py-4 text-xs tracking-[0.2em] uppercase text-white transition-all hover:opacity-85 active:opacity-100"
              style={{ backgroundColor: '#0B1B3D' }}
              data-testid="hero-cta-products"
            >
              Explore Products
            </Link>
            <Link
              to="/contact"
              className="px-8 py-4 text-xs tracking-[0.2em] uppercase transition-all hover:bg-[#0B1B3D]/5"
              style={{ color: '#0B1B3D', border: '1px solid rgba(11,27,61,0.25)' }}
              data-testid="hero-cta-contact"
            >
              Request a Quote
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function CategoriesSection() {
  const [ref, inView] = useInView();
  return (
    <section className="py-24 md:py-32" style={{ backgroundColor: '#FAF9F6' }} data-testid="categories-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div ref={ref} className="mb-14" style={reveal(inView)}>
          <p className="text-xs tracking-[0.3em] uppercase mb-3 font-medium" style={{ color: '#D4AF37' }}>Our Range</p>
          <h2 className="font-light" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#0B1B3D', fontSize: 'clamp(2rem, 4vw, 3rem)' }}>
            Engineered for Beauty
          </h2>
          <p className="text-base font-light mt-3 max-w-md" style={{ color: '#4A4F5A' }}>
            Five core product families. Thousands of possibilities.
          </p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {CATEGORIES.map((cat, idx) => (
            <CategoryCard key={cat.id} category={cat} delay={idx * 80} />
          ))}
        </div>
      </div>
    </section>
  );
}

function CategoryCard({ category, delay }) {
  const [ref, inView] = useInView();
  return (
    <Link
      to={`/products?cat=${category.id}`}
      ref={ref}
      className="group block overflow-hidden"
      style={{ ...reveal(inView), transitionDelay: `${delay}ms` }}
      data-testid={`category-card-${category.id}`}
    >
      <div className="relative aspect-[3/4] overflow-hidden bg-white">
        <img
          src={category.image}
          alt={category.name}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-400" />
      </div>
      <div className="pt-4 pb-2">
        <h3 className="font-medium text-sm tracking-wide" style={{ color: '#0B1B3D' }}>{category.name}</h3>
        <p className="text-xs mt-1 font-light" style={{ color: '#4A4F5A' }}>{category.desc}</p>
      </div>
    </Link>
  );
}

function StatsSection() {
  const [ref, inView] = useInView();
  return (
    <section className="py-20" style={{ backgroundColor: '#0B1B3D' }} data-testid="stats-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div ref={ref} className="grid grid-cols-2 md:grid-cols-4 gap-10" style={reveal(inView)}>
          {STATS.map(stat => (
            <div key={stat.value} className="text-center" data-testid={`stat-${stat.label.toLowerCase().replace(/ /g, '-')}`}>
              <div
                className="font-light mb-2"
                style={{ fontFamily: 'Cormorant Garamond, serif', color: '#D4AF37', fontSize: 'clamp(2.5rem, 5vw, 4rem)' }}
              >
                {stat.value}
              </div>
              <p className="text-xs tracking-[0.15em] uppercase" style={{ color: 'rgba(255,255,255,0.55)' }}>{stat.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ServicesSection() {
  const [ref, inView] = useInView();
  return (
    <section className="py-24 md:py-32" style={{ backgroundColor: '#FFFFFF' }} data-testid="services-preview-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div ref={ref} className="mb-14" style={reveal(inView)}>
          <p className="text-xs tracking-[0.3em] uppercase mb-3 font-medium" style={{ color: '#D4AF37' }}>What We Offer</p>
          <h2 className="font-light" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#0B1B3D', fontSize: 'clamp(2rem, 4vw, 3rem)' }}>
            Value-Added Services
          </h2>
          <p className="text-base font-light mt-3 max-w-md" style={{ color: '#4A4F5A' }}>
            Beyond packaging — we bring craft, technology and artistry to every product.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {SERVICES_PREVIEW.map((svc, idx) => (
            <ServiceCard key={svc.title} service={svc} delay={idx * 100} />
          ))}
        </div>
        <div className="mt-10">
          <Link
            to="/services"
            className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase transition-colors hover:gap-3"
            style={{ color: '#0B1B3D' }}
            data-testid="services-view-all-link"
          >
            View All Services <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </section>
  );
}

function ServiceCard({ service, delay }) {
  const [ref, inView] = useInView();
  return (
    <div
      ref={ref}
      className="group"
      style={{ ...reveal(inView), transitionDelay: `${delay}ms` }}
      data-testid={`service-card-${service.title.toLowerCase().replace(/ /g, '-')}`}
    >
      <div className="relative aspect-video overflow-hidden mb-5">
        <img src={service.image} alt={service.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
      </div>
      <div className="pb-1" style={{ borderTop: '2px solid #D4AF37', paddingTop: '1rem' }}>
        <h3 className="font-medium mb-2" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#0B1B3D', fontSize: '1.25rem' }}>{service.title}</h3>
        <p className="text-sm font-light leading-relaxed" style={{ color: '#4A4F5A' }}>{service.desc}</p>
      </div>
    </div>
  );
}

function AboutSection() {
  const [ref, inView] = useInView();
  return (
    <section className="py-24 md:py-32" style={{ backgroundColor: '#FAF9F6' }} data-testid="about-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-2 gap-16 items-center">
          <div ref={ref} style={reveal(inView)}>
            <p className="text-xs tracking-[0.3em] uppercase mb-4 font-medium" style={{ color: '#D4AF37' }}>Our Story</p>
            <h2 className="font-light mb-6 leading-tight" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#0B1B3D', fontSize: 'clamp(2rem, 4vw, 3rem)' }}>
              Precision Crafted.<br />Globally Trusted.
            </h2>
            <p className="text-base font-light leading-relaxed mb-5" style={{ color: '#4A4F5A' }}>
              For over 25 years, Vimal Plastics has been at the forefront of plastic packaging innovation. From our world-class manufacturing facilities in India, we deliver packaging that meets the highest global standards.
            </p>
            <p className="text-base font-light leading-relaxed mb-8" style={{ color: '#4A4F5A' }}>
              Our commitment to quality, innovation and sustainability has made us the preferred packaging partner for leading cosmetic and beauty brands across the globe.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase transition-all hover:gap-3"
              style={{ color: '#0B1B3D' }}
              data-testid="about-cta-link"
            >
              Partner With Us <ArrowRight size={14} />
            </Link>
          </div>
          <div className="relative overflow-hidden" style={{ aspectRatio: '4/5' }}>
            <img src={IMAGES.about} alt="Premium packaging products" className="w-full h-full object-cover" />
            <div className="absolute bottom-6 left-6 right-6 p-5" style={{ backgroundColor: 'rgba(11,27,61,0.88)', backdropFilter: 'blur(4px)' }}>
              <p className="text-xs tracking-[0.2em] uppercase mb-1" style={{ color: '#D4AF37' }}>Our Promise</p>
              <p className="text-sm text-white font-light" style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.1rem' }}>
                "Made in India with Global Standards"
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function CTASection() {
  const [ref, inView] = useInView();
  return (
    <section className="py-24" style={{ backgroundColor: '#0B1B3D' }} data-testid="cta-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 text-center" ref={ref} style={reveal(inView)}>
        <p className="text-xs tracking-[0.3em] uppercase mb-4 font-medium" style={{ color: '#D4AF37' }}>Get Started</p>
        <h2 className="font-light mb-6 text-white" style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(2rem, 4vw, 3rem)' }}>
          Ready to Elevate Your Brand's Packaging?
        </h2>
        <p className="text-base font-light mb-10 max-w-lg mx-auto" style={{ color: 'rgba(255,255,255,0.65)' }}>
          Share your requirements with us and let's craft the perfect packaging solution for your brand.
        </p>
        <Link
          to="/contact"
          className="inline-block px-10 py-4 text-xs tracking-[0.2em] uppercase transition-all hover:opacity-85"
          style={{ backgroundColor: '#D4AF37', color: '#0B1B3D', fontWeight: '600' }}
          data-testid="cta-enquire-button"
        >
          Request a Quote
        </Link>
      </div>
    </section>
  );
}
